import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, UserPlus, GraduationCap, Clock } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { parseApiError } from '../../lib/apiErrors';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';

const RegisterElevePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Erreur', description: 'Les mots de passe ne correspondent pas', variant: 'destructive' });
      return;
    }

    if (formData.password.length < 6) {
      toast({ title: 'Erreur', description: 'Le mot de passe doit contenir au moins 6 caractères', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/register-eleve', {
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        password: formData.password,
        role: 'eleve'
      });
      setSuccess(true);
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: parseApiError(error),
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#2C3C65] mb-4">Demande envoyée !</h1>
            <p className="text-[#6777A1] mb-6">
              Votre demande d'accès à l'espace élève a été enregistrée.
              Vous recevrez une notification une fois votre compte approuvé par l'école.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#2C3C65]"
            >
              Retour à l'accueil
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4" data-testid="register-eleve-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#285BBB] rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Accès Espace Élève</h1>
            <p className="text-[#6777A1] mt-2">Demander l'accès à votre espace</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-700">
              <strong>Note :</strong> Votre demande sera examinée par l'école.
              Une fois approuvée, vous pourrez accéder à votre espace élève avec le forum et les documents de votre groupe.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nom</label>
                <Input
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  data-testid="eleve-nom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Prénom</label>
                <Input
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  required
                  data-testid="eleve-prenom"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="eleve-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Mot de passe</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="pr-10"
                  data-testid="eleve-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-[#285BBB] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#285BBB] z-10 transition-colors"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  data-testid="register-eleve-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-1">Confirmer le mot de passe</label>
              <Input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                data-testid="eleve-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#285BBB] hover:bg-[#2C3C65] text-white py-3 mt-6"
              data-testid="eleve-submit"
            >
              {loading ? 'Envoi...' : (
                <>
                  <UserPlus className="h-5 w-5 mr-2" />
                  Demander l'accès
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#6777A1]">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-[#285BBB] font-medium hover:underline">
                Se connecter
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-[#6777A1] hover:text-[#285BBB]">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterElevePage;
